import { createSlice } from "@reduxjs/toolkit";
import { set } from "lodash";
import { setProp } from "Store/Actions";


let initialState = {
    sideBarOpen: false,
    openedGroupIndices: [0, 1, 2],
    globalSearchText: "",
    localSearchText: "",
    columnFilter: {},
    showColumnDialog: false,
    activeColumn: "",
    activeColumnData: {},
    dialogPosition: { top: 0, left: 0 },
    tabSelected: "",
    resetFilter: false,
    lastColumnName: "",
    loggedInUserDetails: {},
    // Top-level contract page tab index (legacy ContractMainTabs; CommonContractsTabs is primary UX)
    contractMainTab: 0,
    // Store separate filters for each contract tab
    contractTabFilters: {
        "Contract Hub": {},
        "Agreement Template": {},
        "Frame agreement": {},
        "Esign": {}
    },
    commonOrderTabDetails: {
        selectedTab: 0,
        selectedTabName: "",
        tabData: [],
        tabContentComponents: []
    },
    commonProductTabDetails: {
        selectedTab: 0,
        selectedTabName: "",
        tabData: [],
        tabContentComponents: []
    },
    commonContractTabDetails: {
        selectedTab: 0,
        selectedTabName: "",
        tabData: [],
        tabContentComponents: []
    },
    commonOnboardingTabDetails: {
        selectedTab: 0,
        selectedTabName: "",
        tabData: [],
        tabContentComponents: []
    }
}

const commonSlice = createSlice({
    name: "common",
    initialState,
    reducers: {
        setGlobalSearchText: (state, action) => {
            state.globalSearchText = action.payload;
        },
        clearGlobalSearchText: (state) => {
            state.globalSearchText = '';  // Clear global search text
        },
        setDefaultColumnFilter: (state, action) => {
            state.columnFilter = action.payload;
            // Also update contractTabFilters for current tab if it exists
            const currentTabName = state.commonContractTabDetails.selectedTabName;
            if (currentTabName && state.contractTabFilters[currentTabName] !== undefined) {
                // Only set if the tab filters are empty
                if (Object.keys(state.contractTabFilters[currentTabName]).length === 0) {
                    state.contractTabFilters[currentTabName] = JSON.parse(JSON.stringify(action.payload));
                }
            }
        },
        setColumnFilter: (state, action) => {
            const { columnId, type, value } = action.payload;
            if (!state.columnFilter[columnId]) {
                state.columnFilter[columnId] = {};
            }
            state.columnFilter[columnId][type] = value

            // Also update the contractTabFilters for the current tab
            const currentTabName = state.commonContractTabDetails.selectedTabName;
            if (currentTabName && state.contractTabFilters[currentTabName] !== undefined) {
                if (!state.contractTabFilters[currentTabName][columnId]) {
                    state.contractTabFilters[currentTabName][columnId] = {};
                }
                state.contractTabFilters[currentTabName][columnId][type] = value;
            }
        },
        resetColumnFilter: (state) => {
            state.columnFilter = initialState.columnFilter;
            state.resetFilter = initialState.resetFilter;
            state.activeColumn = initialState.activeColumn;
            state.activeColumnData = initialState.activeColumnData;
            state.lastColumnName = initialState.lastColumnName; // Reset column filter to initial state
            state.showColumnDialog = initialState.showColumnDialog
            state.dialogPosition = initialState.dialogPosition
        },
        setLoggedInUserDetails: (state, action) => {
            state.loggedInUserDetails = action.payload; // Set logged in user details
        },
        setContractMainTab: (state, action) => {
            state.contractMainTab = action.payload;
        },
        setCommonOrderTabs: (state, action) => {
            state.commonOrderTabDetails.tabData = action.payload.tabData;
            state.commonOrderTabDetails.tabContentComponents = action.payload.tabContentComponents;
            // Default selected tab
            if (action.payload.tabData.length > 0) {
                state.commonOrderTabDetails.selectedTab = action.payload.tabData[0].id;
            }
        },
        setSelectedTab: (state, action) => {
            let { id, children } = action.payload;
            state.commonOrderTabDetails.selectedTab = id;
            state.commonOrderTabDetails.selectedTabName = children;
        },
        setCommonProductTabs: (state, action) => {
            state.commonProductTabDetails.tabData = action.payload.tabData;
            state.commonProductTabDetails.tabContentComponents = action.payload.tabContentComponents;
            // Default selected tab
            if (action.payload.tabData.length > 0) {
                state.commonProductTabDetails.selectedTab = action.payload.tabData[0].id;
            }
        },
        setSelectedProductTab: (state, action) => {
            let { id, children } = action.payload;
            state.commonProductTabDetails.selectedTab = id;
            state.commonProductTabDetails.selectedTabName = children;
        },
        setCommonContractTabs: (state, action) => {
            state.commonContractTabDetails.tabData = action.payload.tabData;
            state.commonContractTabDetails.tabContentComponents = action.payload.tabContentComponents;
            // Default selected tab
            if (action.payload.tabData.length > 0) {
                state.commonContractTabDetails.selectedTab = action.payload.tabData[0].id;
            }
        },
        setSelectedContractTab: (state, action) => {
            let { id, children } = action.payload;

            // Save current tab's filters before switching
            const currentTabName = state.commonContractTabDetails.selectedTabName;
            if (currentTabName && state.contractTabFilters[currentTabName] !== undefined) {
                state.contractTabFilters[currentTabName] = JSON.parse(JSON.stringify(state.columnFilter));
            }

            // Update selected tab
            state.commonContractTabDetails.selectedTab = id;
            state.commonContractTabDetails.selectedTabName = children;

            // Restore the new tab's filters
            if (children && state.contractTabFilters[children] !== undefined) {
                state.columnFilter = JSON.parse(JSON.stringify(state.contractTabFilters[children]));
            } else {
                state.columnFilter = {};
            }

            // Reset dialog states
            state.activeColumn = "";
            state.activeColumnData = {};
            state.showColumnDialog = false;
            state.dialogPosition = { top: 0, left: 0 };
        },

        setCommonOnboardingTabs: (state, action) => {
            state.commonOnboardingTabDetails.tabData = action.payload.tabData;
            state.commonOnboardingTabDetails.tabContentComponents = action.payload.tabContentComponents;
            if (action.payload.tabData.length > 0) {
                state.commonOnboardingTabDetails.selectedTab = 0;
                state.commonOnboardingTabDetails.selectedTabName = action.payload.tabData[0].tab.children;
            }
        },
        setSelectedOnboardingTab: (state, action) => {
            let { id, children } = action.payload;
            state.commonOnboardingTabDetails.selectedTab = id;
            state.commonOnboardingTabDetails.selectedTabName = children;
        }

    },
    extraReducers(builder) {
        builder.addCase(setProp, (state, action: {
            payload: any;
            type: string;
            slice: string;
        }) => {
            if (action.payload.slice === commonSlice.name) {
                state[action.payload.key] = action.payload.value;
            }
        });
    }
});



export default commonSlice.reducer;
export const { setCommonContractTabs, setSelectedContractTab, setContractMainTab, setCommonOnboardingTabs, setSelectedOnboardingTab,setGlobalSearchText, clearGlobalSearchText, setDefaultColumnFilter, setColumnFilter, setLoggedInUserDetails, resetColumnFilter, setCommonOrderTabs, setSelectedTab, setCommonProductTabs, setSelectedProductTab } = commonSlice.actions;
